import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataProvider } from "./context/DataContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminServices from "./pages/AdminServices";
import AdminCategories from "./pages/AdminCategories";
import ActivityLogs from "./pages/ActivityLogs";
import MitarbeiterAbgleich from "./pages/MitarbeiterAbgleich";
import Reports from "./pages/Reports";
import ExclusionList from "./pages/ExclusionList";
import CsvTransformer from "./pages/CsvTransformer";
import AuditReport from "./pages/AuditReport";
import ServicePlaceholder from "./pages/ServicePlaceholder";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    // All users (including admins) go to dashboard after login
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Init Admin on App Load
const InitAdmin = () => {
  useEffect(() => {
    const initAdmin = async () => {
      try {
        await axios.post(`${API}/init-admin`);
      } catch (error) {
        // Admin already exists or other error - ignore
      }
    };
    initAdmin();
  }, []);

  return null;
};

function AppRoutes() {
  return (
    <>
      <InitAdmin />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/activity"
          element={
            <ProtectedRoute adminOnly>
              <ActivityLogs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/services"
          element={
            <ProtectedRoute adminOnly>
              <AdminServices />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/categories"
          element={
            <ProtectedRoute adminOnly>
              <AdminCategories />
            </ProtectedRoute>
          }
        />

        {/* Service Routes */}
        <Route
          path="/service/mitarbeiterabgleich"
          element={
            <ProtectedRoute>
              <MitarbeiterAbgleich />
            </ProtectedRoute>
          }
        />

        <Route
          path="/service/gesamtabsolvierungen"
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          }
        />

        {/* Exclusion List Route */}
        <Route
          path="/exclusion-list"
          element={
            <ProtectedRoute>
              <ExclusionList />
            </ProtectedRoute>
          }
        />

        {/* CSV Transformer Route */}
        <Route
          path="/service/csv_transformer"
          element={
            <ProtectedRoute>
              <CsvTransformer />
            </ProtectedRoute>
          }
        />

        {/* Audit Report Route */}
        <Route
          path="/service/audit-report"
          element={
            <ProtectedRoute>
              <AuditReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/service/:serviceId"
          element={
            <ProtectedRoute>
              <ServicePlaceholder />
            </ProtectedRoute>
          }
        />

        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
